import { NavLink } from "react-router-dom";
import { useEffect } from "react";
import * as amplitude from "@amplitude/analytics-browser";
import { socialProfiles } from "./content";
import usePageMetadata from "./usePageMetadata";

const socialNames = ["YouTube", "Facebook", "TikTok", "Instagram"];

export default function AboutMathMinds() {
    usePageMetadata({
        title: "About MathMinds and WitPlex LLC | MathMinds",
        description: "Learn about WitPlex LLC, the company behind MathMinds math and brain games for children, and find official support and social links.",
        canonicalPath: "/about",
    });

    useEffect(() => {
        amplitude.track("app_start_about");
    }, []);

    return (
        <article className="mx-auto max-w-[1000px] px-4 py-10 sm:px-8 sm:py-12 lg:py-16">
            <header className="grid items-center gap-8 md:grid-cols-[1fr_280px] md:gap-10">
                <div>
                    <p className="font-bold uppercase tracking-[0.18em] text-[#C37C00]">About us</p>
                    <h1 className="mt-3 break-words font-chango text-3xl leading-tight text-[#327560] min-[380px]:text-4xl sm:text-5xl">About MathMinds and WitPlex LLC</h1>
                    <p className="mt-5 text-base leading-7 text-[#56635E] sm:mt-6 sm:text-lg sm:leading-8">MathMinds is a math and brain-games app for children, developed by <a href="https://www.witplex.com/" target="_blank" rel="noreferrer" className="font-semibold text-[#327560] underline">WitPlex LLC</a>.</p>
                </div>
                <img src="/backGrounds/logo.svg" alt="MathMinds logo" className="mx-auto w-full max-w-[160px] sm:max-w-[220px]" />
            </header>

            <section className="mt-10 rounded-3xl bg-white p-5 shadow-sm sm:mt-12 sm:p-9">
                <h2 className="font-chango text-2xl text-[#327560]">Who creates MathMinds?</h2>
                <p className="mt-5 leading-8 text-[#56635E]"><a href="https://www.witplex.com/" target="_blank" rel="noreferrer" className="font-semibold text-[#327560] underline">WitPlex LLC</a> creates MathMinds to help children practice mathematics, memory, concentration, logic, and problem solving through approachable game-based activities.</p>
                <p className="mt-4 leading-8 text-[#56635E]">The educational purpose of MathMinds is to give families a flexible way to reinforce skills at an appropriate level—from early counting and number recognition to multiplication, division, fractions, decimals, and brain-training activities.</p>
            </section>

            <section className="mt-10 grid gap-6 md:grid-cols-2">
                <div className="min-w-0 rounded-3xl bg-[#DDF5ED] p-5 sm:p-7">
                    <h2 className="font-chango text-2xl text-[#327560]">Official website and support</h2>
                    <dl className="mt-5 space-y-4 leading-7">
                        <div><dt className="font-bold">Company</dt><dd><a href="https://www.witplex.com/" target="_blank" rel="noreferrer" className="text-[#327560] underline">WitPlex LLC</a></dd></div>
                        <div><dt className="font-bold">Product</dt><dd>MathMinds</dd></div>
                        <div><dt className="font-bold">Website</dt><dd><a href="https://mathmindsgame.com/" className="break-all text-[#327560] underline">mathmindsgame.com</a></dd></div>
                        <div><dt className="font-bold">Support</dt><dd><a href="mailto:mathgame@witplex.com" className="break-all text-[#327560] underline">mathgame@witplex.com</a></dd></div>
                    </dl>
                </div>
                <div className="min-w-0 rounded-3xl bg-[#FFF6D9] p-5 sm:p-7">
                    <h2 className="font-chango text-2xl text-[#327560]">Official social profiles</h2>
                    <ul className="mt-5 space-y-3">
                        {socialProfiles.map((profile, index) => (
                            <li key={profile}><a href={profile} target="_blank" rel="noreferrer" className="font-bold text-[#327560] underline">MathMinds on {socialNames[index]}</a></li>
                        ))}
                    </ul>
                </div>
            </section>

            <section className="mt-10 rounded-3xl border border-[#E9E2CD] p-5 sm:p-9">
                <h2 className="font-chango text-2xl text-[#327560]">Explore MathMinds</h2>
                <p className="mt-4 leading-7 text-[#56635E]">Read our parent guides to find games by age and skill, or open the game collection to begin playing.</p>
                <div className="mt-6 flex flex-col gap-3 min-[400px]:flex-row min-[400px]:flex-wrap">
                    <NavLink to="/math-games/" className="rounded-full border-2 border-[#327560] px-6 py-3 text-center font-bold text-[#327560]">Parent guides</NavLink>
                    <NavLink to="/games/memory" className="rounded-full bg-[#F9B818] px-6 py-3 text-center font-bold">Play games</NavLink>
                </div>
            </section>
        </article>
    );
}
